import React, { useState, useMemo, useCallback } from 'react';

import Modal from 'components/partials/modal/modal';
import TableComponent from 'components/partials/table-component/table-component';
import type { TableAmountState } from 'components/partials/table-component/table-component';
import Search from 'components/partials/search/search';
import Button from 'components/partials/button/button';
import { Pagination } from 'components/partials/table-component/pagination/pagination';
import { ViewByAmountSelect } from 'components/partials/table-component/view-by-amount-select/view-by-amount-select';

import FundsFilters from './funds-filters';
import FilterTag from './filter-tag';
import { getSelectFundsModalTableSettings } from './select-funds-modal-table-settings';

import { useClientSpecialFunds } from '../../../hooks/use-aid-special-funds';

import {
  FUND_TYPE_OPTIONS,
  FUND_SOURCE_OPTIONS,
} from '../funds-step.constants';
import { EMPTY_FUNDS_FILTERS } from '../funds-step.types';
import type { FundsFilterValues } from '../funds-step.types';
import type { SortingState } from '@tanstack/react-table';

const PAGE_SIZE = 10;

interface FundItem {
  id: string | number;
  type?: string;
  name?: string;
  [key: string]: unknown;
}

interface SelectFundsModalProps {
  userId: string;
  clientId: string | number;
  modelId: string | number;
  excludeIds: (string | number)[];
  onAdd: (funds: FundItem[]) => void;
  onClose: () => void;
}

interface FundQueryFilters {
  id?: { notIn: string };
  type?: string[];
  source?: string[];
  instAid?: boolean;
  totalAid?: boolean;
  name?: { contains: string };
  code?: { equals: string };
}

const SelectFundsModal = ({
  userId,
  clientId,
  modelId,
  excludeIds,
  onAdd,
  onClose,
}: SelectFundsModalProps): React.JSX.Element => {
  const [search, setSearch] = useState<string>('');
  const [appliedFilters, setAppliedFilters] =
    useState<FundsFilterValues>(EMPTY_FUNDS_FILTERS);
  const [selectedFunds, setSelectedFunds] = useState<FundItem[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [sortingParams, setSortingParams] = useState<SortingState>([]);

  const filters = useMemo<FundQueryFilters>(() => {
    const f: FundQueryFilters = {};

    if (excludeIds.length > 0) {
      f.id = { notIn: excludeIds.join(',') };
    }

    if (appliedFilters.type.length > 0) {
      f.type = appliedFilters.type;
    }

    if (appliedFilters.source.length > 0) {
      f.source = appliedFilters.source;
    }

    if (appliedFilters.instAid !== null) {
      f.instAid = appliedFilters.instAid;
    }

    if (appliedFilters.totalAid !== null) {
      f.totalAid = appliedFilters.totalAid;
    }

    if (search) {
      if (/^\d+$/.test(search)) {
        f.code = { equals: search };
      } else {
        f.name = { contains: search };
      }
    }

    return f;
  }, [excludeIds, appliedFilters, search]);

  const queryResult = useClientSpecialFunds({
    userId,
    clientId,
    params: {
      filters,
      pageSize,
      pageIndex,
      modelId,
      sortBy: sortingParams,
    },
    enabled: true,
  }) as unknown as {
    data?: {
      data?: FundItem[];
      pagination?: { totalRecords?: number };
    };
    isLoading: boolean;
    isFetching: boolean;
  };
  const { data, isFetching } = queryResult;

  const pageData: FundItem[] = data?.data || [];
  const totalRecords = data?.pagination?.totalRecords || 0;
  const pagesAmount = Math.ceil(totalRecords / pageSize);

  const paginationAdapter = useMemo(
    () => ({
      getRowModel: () => ({
        rows: pageData.map((row) => ({ ...row, depth: 0 })),
      }),
      setPageIndex: (idx: number) => setPageIndex(idx),
      previousPage: () => setPageIndex((prev) => Math.max(0, prev - 1)),
      nextPage: () => setPageIndex((prev) => prev + 1),
      getPageCount: () => pagesAmount,
      setPageSize: (size: number) => {
        setPageSize(size);
        setPageIndex(0);
      },
    }),
    [pageData, pagesAmount]
  );

  const handleToggleFund = useCallback((fund: FundItem) => {
    setSelectedFunds((prev) =>
      prev.some((f) => f.id === fund.id)
        ? prev.filter((f) => f.id !== fund.id)
        : [...prev, fund]
    );
  }, []);

  const allSelected =
    pageData.length > 0 &&
    pageData.every((f) => selectedFunds.some((s) => s.id === f.id));

  const someSelected =
    pageData.some((f) => selectedFunds.some((s) => s.id === f.id)) &&
    !allSelected;

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      const pageIds = new Set(pageData.map((f) => f.id));

      setSelectedFunds((prev) => prev.filter((f) => !pageIds.has(f.id)));
    } else {
      setSelectedFunds((prev) => {
        const existingIds = new Set(prev.map((f) => f.id));
        const newFunds = pageData.filter((f) => !existingIds.has(f.id));

        return [...prev, ...newFunds];
      });
    }
  }, [allSelected, pageData]);

  const handleApplyFilters = (next: FundsFilterValues): void => {
    setAppliedFilters(next);
    setPageIndex(0);
  };

  const handleRemoveFilter = (
    field: keyof FundsFilterValues,
    value?: string
  ): void => {
    setAppliedFilters((prev) => {
      if (field === 'instAid' || field === 'totalAid') {
        return { ...prev, [field]: null };
      }

      return {
        ...prev,
        [field]: (prev[field] as string[]).filter((v) => v !== value),
      };
    });
    setPageIndex(0);
  };

  const handleClearAllFilters = (): void => {
    setAppliedFilters(EMPTY_FUNDS_FILTERS);
    setPageIndex(0);
  };

  const handleSearchChange = (value: string): void => {
    setSearch(value);
    setPageIndex(0);
  };

  const handleTableSetAmount = useCallback(
    (updater: (state: TableAmountState) => TableAmountState): void => {
      const next = updater({ pageSize, totalRecords });

      setPageSize(next.pageSize);
      setPageIndex(0);
    },
    [pageSize, totalRecords]
  );

  const columns = useMemo(
    () =>
      getSelectFundsModalTableSettings({
        selectedIds: selectedFunds.map((f) => f.id),
        onToggle: handleToggleFund,
        onToggleAll: handleToggleAll,
        allSelected,
        someSelected,
      }),
    [
      selectedFunds,
      handleToggleFund,
      handleToggleAll,
      allSelected,
      someSelected,
    ]
  );

  const footer = (
    <div className="flex items-center gap-5">
      <Button
        type="button"
        variant="brand-filled"
        size="md"
        disabled={selectedFunds.length === 0}
        onClick={() => onAdd(selectedFunds)}
        data-testid="add-selected-button"
      >
        Add selected
      </Button>
      <Button
        type="button"
        variant="reserve-text"
        size="md"
        onClick={onClose}
        data-testid="cancel-modal-button"
      >
        Cancel
      </Button>
    </div>
  );

  return (
    <Modal
      headerText="Select funds to add"
      onCloseCallback={onClose}
      modalClassName="!w-[1150px] !max-w-[1150px] !max-h-[90vh] !overflow-visible"
      footer={footer}
      showCloseButton
      data-testid="select-funds-modal"
    >
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-5 mt-4 mb-4 shrink-0">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Search
              setSearch={handleSearchChange}
              placeholder="Search..."
              customClass="flex-1 !max-h-11"
            />
            <FundsFilters
              values={appliedFilters}
              onApply={handleApplyFilters}
            />
          </div>

          <div className="flex items-center gap-6 shrink-0">
            <Pagination
              table={paginationAdapter as never}
              pagesAmount={pagesAmount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRecords={totalRecords}
            />
            <ViewByAmountSelect
              table={paginationAdapter as never}
              pageSize={pageSize}
              setAmount={handleTableSetAmount}
            />
          </div>
        </div>

        {(appliedFilters.source.length > 0 ||
          appliedFilters.type.length > 0 ||
          appliedFilters.instAid !== null ||
          appliedFilters.totalAid !== null) && (
          <div className="flex items-center gap-3 mb-4 shrink-0 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">
              Applied filters:
            </span>
            <div className="flex items-center gap-3 flex-wrap">
              {appliedFilters.source.map((src) => (
                <FilterTag
                  key={`source-${src}`}
                  label={`Source: ${
                    FUND_SOURCE_OPTIONS.find((o) => o.value === src)?.label ||
                    src
                  }`}
                  onRemove={() => handleRemoveFilter('source', src)}
                />
              ))}
              {appliedFilters.type.map((t) => (
                <FilterTag
                  key={`type-${t}`}
                  label={`Type: ${
                    FUND_TYPE_OPTIONS.find((o) => o.value === t)?.label || t
                  }`}
                  onRemove={() => handleRemoveFilter('type', t)}
                />
              ))}
              {appliedFilters.instAid !== null && (
                <FilterTag
                  label={`Inst. aid: ${appliedFilters.instAid ? 'Yes' : 'No'}`}
                  onRemove={() => handleRemoveFilter('instAid')}
                />
              )}
              {appliedFilters.totalAid !== null && (
                <FilterTag
                  label={`Total aid: ${appliedFilters.totalAid ? 'Yes' : 'No'}`}
                  onRemove={() => handleRemoveFilter('totalAid')}
                />
              )}
              <Button
                type="button"
                variant="reserve-text-link"
                size="sm"
                onClick={handleClearAllFilters}
                data-testid="clear-all-filters"
              >
                Clear All
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden min-h-0">
          <TableComponent
            columns={columns}
            data={pageData}
            isLoading={isFetching}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPagination={setPageIndex}
            setAmount={handleTableSetAmount}
            sortingParams={sortingParams}
            setSortingParams={(params) => {
              setSortingParams(params);
              setPageIndex(0);
            }}
            manualSorting
            totalRecords={totalRecords}
            pagination={false}
            customHeight="max-h-[50vh]"
          />
        </div>

        {pageData.length === 0 && !isFetching && (
          <div className="text-center py-8 text-gray-500">
            No funds available to add
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SelectFundsModal;
